import { CEILINGS, getThresholds } from './settings.js'

// All three exported functions take an optional `ceilings` object
// ({Emax, Cmax}) so callers can score each zone against its own
// resolved ceiling instead of the legacy global default. When omitted,
// behaviour is identical to the pre-Sprint-4 module-level lookups.

export function computeHeadroom(z, ceilings = CEILINGS) {
  return {
    eHead: Math.max(0, ceilings.Emax - z.engagementRate),
    cHead: Math.max(0, ceilings.Cmax - z.conversionRate),
  }
}

export function classifyPattern(z, ceilings = CEILINGS) {
  const T = getThresholds(ceilings)
  const { eHead, cHead } = computeHeadroom(z, ceilings);
  const e = z.engagementRate, c = z.conversionRate, v = z.visitors;

  // Note: labels here intentionally omit icons. Icons live in PATTERN_META.icon
  // and renderers prefix them where needed, so the text stays clean and easy
  // to compose into sentences.
  if (v >= T.MIN_VISITORS && e <= (T.LOW_ENGAGEMENT - T.HYST)) {
    return {
      kind: 'awareness',
      label: 'Engagement gap — visitors enter but don\'t stop',
      class: 'pat-awareness',
      suggestion: 'Improve storytelling/signage, stronger hero SKU, simplify steps.'
    }
  }

  if (e >= (T.HIGH_ENGAGEMENT + T.HYST) && c <= (T.LOW_CONVERSION - T.HYST)) {
    return {
      kind: 'attention',
      label: 'Conversion gap — engaged visitors don\'t buy',
      class: 'pat-attention',
      suggestion: 'Add CTA/price clarity, staff assist at peaks, remove friction.'
    }
  }

  if (v < T.MIN_VISITORS && e >= (T.HIGH_ENGAGEMENT + T.HYST)) {
    return {
      kind: 'reach',
      label: 'Reach gap — engages well, few find it',
      class: 'pat-reach',
      suggestion: 'Move closer to main flow, cue signage, fix sightlines.'
    }
  }

  // Close-to-ceiling on both → "Performing well"
  const closeToCeiling = (eHead <= T.GOOD_E_HEADROOM) && (cHead <= T.GOOD_C_HEADROOM);
    if (closeToCeiling) {
      return {
        kind: 'strong',
        label: 'Performing well — at or near ceiling on both engagement and conversion',
        class: 'pat-strong',
        suggestion: 'Protect layout; consider cross-sell/attach tactics.'
      }
    }

    // Neutral default — middle of the road, not bad enough to flag a gap.
    return {
      kind: 'neutral',
      label: 'Mid-tier — solid baseline with room for incremental gains',
      class: 'pat-neutral',
      suggestion: 'Iterate: visual cues, price clarity, attach offers.'
    }
  }

// computePotentialEUR (the old +5pp/+1pp winner-take-all € lever) was DELETED
// in the four-rung rework. "Within reach" € is now the gap from current to the
// active benchmark rung, computed per-lever in ladder.js (computeLadder) and
// consumed by priorities.js. See docs/06_four_rung_benchmark_model.md §2.4.

// Ladder-anchored pattern for the Prioritization surface (§2.7). Unlike
// classifyPattern (above), which scores a zone against its sector CEILING and
// is kept for the strategic-quadrant frame, this maps the resolved ladder to a
// pattern: the biggest-€ actionable lever names the gap, and a zone at/above
// its top available rung is "strong". "neutral" therefore all but disappears —
// every zone with room has a concrete next rung and a named lever.
//
// `ladder` is the object returned by computeLadder(z, peers, ceilings, cohort).
// The kind values are the SAME enum classifyPattern uses, so PATTERN_META,
// pickActions() and every downstream consumer keep working unchanged.
const _PATTERN_BY_LEVER = {
  engagement: {
    kind: 'awareness',
    label: 'Engagement gap — visitors enter but don\'t stop',
    class: 'pat-awareness',
    suggestion: 'Improve storytelling/signage, stronger hero SKU, simplify steps.',
  },
  conversion: {
    kind: 'attention',
    label: 'Conversion gap — engaged visitors don\'t buy',
    class: 'pat-attention',
    suggestion: 'Add CTA/price clarity, staff assist at peaks, remove friction.',
  },
  reach: {
    kind: 'reach',
    label: 'Reach gap — engages well, few find it',
    class: 'pat-reach',
    suggestion: 'Move closer to main flow, cue signage, fix sightlines.',
  },
}

export function patternFromLadder(ladder) {
  const h = ladder && ladder.headline
  // platform_capabilities.md §17.7 Phase 2 — a counts-only (tripwire) zone can't measure engagement, so it
  // must NEVER be labelled "performing well" (which implies measured engagement +
  // conversion at ceiling). Classify it on reach/traffic terms instead: a reach
  // headline names the reach gap; otherwise it's a plain traffic zone.
  if (ladder && ladder.engagementMeasurable === false) {
    if (h && h.lever === 'reach') return _PATTERN_BY_LEVER.reach
    return {
      kind: 'traffic',
      label: 'Traffic zone — engagement isn\'t measured here',
      class: 'pat-traffic',
      suggestion: 'Tripwire-only zone: track footfall and flow; add a polygon to measure engagement.',
    }
  }
  if (!h || ladder.atCeiling) {
    // No actionable lever with € to a higher rung → holding at the top rung.
    return {
      kind: 'strong',
      label: 'Performing well — at or above your top available benchmark rung',
      class: 'pat-strong',
      suggestion: 'Protect layout; consider cross-sell/attach tactics.',
    }
  }
  return _PATTERN_BY_LEVER[h.lever] || {
    kind: 'neutral',
    label: 'Mid-tier — solid baseline with room for incremental gains',
    class: 'pat-neutral',
    suggestion: 'Iterate: visual cues, price clarity, attach offers.',
  }
}
